import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, ProgressBar, Stack, t, Text } from '@kanbios/ui';
import type { serverAccess } from '@kanbios/api-client';
import { api, unwrap } from '../lib/api';

type Status = serverAccess.components['schemas']['AccessCheckStatus'];

/** Beyond the check's own time limit (10 min): the portal stops waiting and says so. */
const MAX_WAIT_MS = 12 * 60_000;

/**
 * « Vérifier l'accès »: asks for a check, then follows it until its result —
 * the progress bar while it runs, then what it found or why it failed. The
 * status lives in the backend's database, whichever instance answers.
 *
 * D59: the check followed always starts after the click (the backend never
 * answers with the one running), and `run(servers)` checks those servers only.
 * Asked again while waiting, the asks add up: the backend merges them into
 * the same next check.
 */
export function useAccessCheck() {
  const queryClient = useQueryClient();
  const [target, setTarget] = useState<{ generation: number; since: number; servers: string[] | null } | null>(null);
  const [dismissed, setDismissed] = useState(false);
  // The check followed has ended: its own result, whatever ran after it.
  const isFinished = (s: Status | undefined) => Boolean(target && s?.last && s.last.generation >= target.generation);
  const status = useQuery({
    queryKey: ['server-access', 'check'],
    queryFn: async () => unwrap(await api.serverAccess.GET('/servers/check')),
    enabled: target !== null,
    refetchInterval: query => (isFinished(query.state.data) ? false : 2_000),
  });
  const start = useMutation({
    mutationFn: async (servers: string[] | null) =>
      unwrap(await api.serverAccess.POST('/servers/check', servers ? { body: { servers } } : {})),
    onSuccess: r => {
      setDismissed(false);
      // Follow the new check now, even when the last one was followed to its end.
      void queryClient.invalidateQueries({ queryKey: ['server-access', 'check'] });
      setTarget(previous => {
        const waiting = previous && !(isFinished(status.data) || Date.now() - previous.since > MAX_WAIT_MS);
        const servers = r.servers ?? null;
        if (!waiting) return { generation: r.generation, since: Date.now(), servers };
        return {
          generation: Math.max(r.generation, previous.generation),
          since: Date.now(),
          servers: previous.servers === null || servers === null ? null : [...new Set([...previous.servers, ...servers])],
        };
      });
    },
  });
  const finished = isFinished(status.data);
  const timedOut = Boolean(target && !finished && Date.now() - target.since > MAX_WAIT_MS);
  const waiting = Boolean(target && !finished && !timedOut);
  // The states found are on the servers list and pages: read them again once the check ends.
  useEffect(() => {
    if (finished) void queryClient.invalidateQueries({ predicate: q => q.queryKey[0] !== 'server-access' });
  }, [finished, queryClient]);
  return {
    /** Every server, or these ones only (D59). */
    run: (servers?: string[]) => {
      if (start.isPending) return;
      // Every server: one ask at a time. A few servers: asked again, they join the next check.
      if (!servers && waiting) return;
      start.mutate(servers?.length ? servers : null);
    },
    running: start.isPending || waiting,
    /** This server is in the check being followed (or being asked). */
    checking: (server: string) =>
      (start.isPending && (start.variables === null || Boolean(start.variables?.includes(server))))
      || (waiting && (target!.servers === null || target!.servers.includes(server))),
    feedback: target && !dismissed
      ? {
        status: status.data, finished, timedOut, dismiss: () => setDismissed(true),
        // Asked while another check runs: ours starts when it ends.
        queued: Boolean(status.data && status.data.generation < target.generation),
      }
      : null,
  };
}

export type AccessCheckFeedback = ReturnType<typeof useAccessCheck>['feedback'];

/** The progress of the check, then its result, until dismissed. */
export function AccessCheckStatus({ feedback }: { feedback: AccessCheckFeedback }) {
  if (!feedback) return null;
  const { status, finished, timedOut, dismiss, queued } = feedback;
  const close = { action: t('access.check.dismiss'), onAction: dismiss };
  if (timedOut) return <Alert tone="warn" title={t('access.check.timedOut')} {...close} />;
  if (!finished || !status?.last) {
    const progress = status?.progress;
    const total = progress?.total ?? 0;
    return (
      <Alert tone="info" title={t('access.check.running')}>
        <Stack gap={8}>
          <ProgressBar
            value={!queued && total ? ((progress?.done ?? 0) / total) * 100 : 0}
            label={queued ? t('access.check.queued') : total ? t('access.check.progress', { n: progress?.done ?? 0, total }) : t('access.check.preparing')}
            ariaLabel={t('access.check.running')}
          />
        </Stack>
      </Alert>
    );
  }
  const last = status.last;
  if (last.outcome === 'done') {
    return (
      <Alert tone={last.trusted === last.total ? 'pos' : 'info'} title={t('access.check.done')} {...close}>
        {t('access.check.doneBody', { n: last.trusted ?? 0, total: last.total ?? 0 })}
      </Alert>
    );
  }
  if (last.outcome === 'blocked') {
    return <Alert tone="warn" title={t('access.check.blocked')} {...close}>{t('access.check.blockedBody', { ip: last.blockedFrom ?? '' })}</Alert>;
  }
  const engine = /^Ansible engine unreachable/.test(last.error ?? '');
  return (
    <Alert tone="neg" title={t('access.check.failed')} {...close}>
      <Stack gap={4}>
        <Text>{engine ? t('access.check.engineUnreachable') : t('access.check.failedBody')}</Text>
        <Text variant="body-sm" tone="mute">{last.error}</Text>
      </Stack>
    </Alert>
  );
}
